import { Trans } from '@frontend/i18n';
import { currency, Text } from '@frontend/design-system';
import { useCareCreditTranslations } from '../../../../hooks/use-care-credit-translations';
import { styles, FinanceOptionDetailsProps } from './finance-option-details-shared';

export const FinanceOptionDetailsDeferredInterest = ({ financeOption }: FinanceOptionDetailsProps) => {
  const { t } = useCareCreditTranslations();

  const isPaymentRequired = financeOption.totalAmount > financeOption.minAmount;
  return (
    <div className='finance-option-details__deferred-interest' css={styles.root}>
      <div className='totals' css={styles.totals}>
        <div>
          <Text>{t('Amount Financed')}</Text>
          <Text weight='bold'>{currency(financeOption.totalAmount, { decimals: 2 })}</Text>
        </div>
        <div>
          <Text>{isPaymentRequired ? t('Required Payments') : t('Suggested Payments')}</Text>
          <Text weight='bold'>
            {t('{{amount}}/month', {
              amount: currency(financeOption.requiredPayment, { decimals: 0 }),
            })}
          </Text>
        </div>
        <div>
          <Text>{t('Promo Period')}</Text>
          <Text weight='bold'>{t('{{months}} months', { months: financeOption.period })}</Text>
        </div>
      </div>

      <Text weight='bold'>
        <Trans t={t}>
          The monthly payment shown may be greater than the required minimum monthly payment that will be on your
          billing statement when you use the promo financing offer.
        </Trans>
      </Text>

      <Text>
        <Trans t={t}>
          <ul>
            <li>
              This estimated payment: Equals the greater of (a) the Amount Financed divided by the number of months in
              the promo period rounded to the next highest whole dollar, in which case it is only a Suggested Payment,
              or (b) $30, the currently required minimum monthly payment.
            </li>
            <li>
              Would pay off the Amount Financed within the Payoff Period, but only if there are no other balances on
              your account at any time during the promo period and you make your payments on time. Your total payments
              will equal the Amount Financed
            </li>
          </ul>
        </Trans>
      </Text>

      <Text weight='bold'>
        <Trans t={t}>
          If the Amount Financed is not paid in full within the promo period, interest will be charged to your account
          from the purchase date and your total payments will be greater than the Amount Financed.
        </Trans>
      </Text>
      <Text>
        <Trans t={t}>
          IMPORTANT: The information about the monthly payment shown assumes the promo financing offer above is applied
          to the Amount Financed:
        </Trans>
      </Text>

      <Text weight='bold'>
        {t('No Interest if Paid in Full Within {{months}} Months*', {
          months: financeOption.period,
        })}
      </Text>

      <Text>
        <Trans t={t}>
          * No interest will be charged on the promo balance if you pay it off, in full, within the promo period. If you
          do not, interest will be charged on the promo balance from the purchase date. The required minimum monthly
          payments may or may not pay off the promo balance before the end of the promo period, depending on purchase
          amount, promo length and payment allocation. Regular account terms apply to non-promo purchases and, after
          promo period ends, to the promo balance. New Accounts as of 5/30/2024: Purchase APR is 32.99%. Penalty APR is
          39.99%. Minimum Interest Charge is $2. Existing cardholders: See your credit card agreement terms. Subject to
          credit approval. We reserve the right to discontinue or alter the terms of this offer at any time.
        </Trans>
      </Text>
    </div>
  );
};
